import {ChangeDetectionStrategy, Component, Input, ViewChild} from '@angular/core';
import {AladinSkyLayersService} from '../aladin-layers/aladin-sky-layers.service';
import {combineLatest, Observable, of, Subject} from 'rxjs';


import {debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {AladinService} from '../aladin.service';
import {AladinFootprintsService} from '../aladin-footprints.service';
import {AladinSkyObjectsService, SkyObject} from '../aladin-sky-objects.service';
import {AladinCellsService} from '../aladin-cells.service';
import {AladinSkyComponent} from '../aladin-sky/aladin-sky.component';
import {
  AsaxHeaderService,
  AsaxResultTableService,
  AsaxSkyViewService,
  AsaxVoService,
  Footprint,
  SourceNameSearchRadius
} from 'asax-common';
import {AsaxDownloadingService} from 'asax-downloading';
import {SkyLayer} from "../aladin-layers/aladin-sky-layers-options/aladin-sky-layers-options.component";
import {VOServiceItem, VOServiceOverlay} from "../aladin-vo/aladin-vo.component";

const headerSearchFiltersMappingSearchProperties = {
  // Header search filters mapping search properties
  //    Position
  sourceNameResolver: {property: 'footprint'},
  sourceName: {property: 'sourceName'},
  raDec: {property: {ra: 'ra', dec: 'dec', footprint: 'footprint'}},
  galactic: {
    property: {
      longitude: 'galacticLongitude',
      latitude: 'galacticLatitude',
      ra: 'ra',
      dec: 'dec',
      footprint: 'footprint'
    }
  },
  targetList: {property: {sourceName: 'sourceName', ra: 'ra', dec: 'dec', footprint: 'footprint'}},
  spatialResolution: {property: 'spatialResolution'},
  largestAngularScale: {property: 'largestAngularScale'},

  //    Energy
  frequency: {
    path: 'energy',
    property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
    isNested: true
  },
  band: {property: 'band'},
  spectralResolution: {path: 'energy', property: 'energy.resMin', isNested: true},
  continuumSensitivity: {property: 'continuumSensitivity'},
  lineSensitivity: {property: 'lineSensitivity'},

  //    Project
  projectCode: {property: 'project.code'},
  projectTitle: {property: 'project.title'},
  projectAbstract: {property: 'project.proposalAbstract'},
  piName: {property: 'project.piName'},
  proposalAuthors: {property: 'project.coiName'},
  scienceKeywordIds: {property: 'project.scienceKeywordIds'},

  //    Publication
  bibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
  title: {path: 'publications', property: 'publications.title', isNested: true},
  bibAbstract: {path: 'publications', property: 'publications.bibAbstract', isNested: true},
  firstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
  authors: {path: 'publications', property: 'publications.authors', isNested: true},

  //    Observations
  observationDate: {property: 'observationDate'},
  polarisationType: {path: 'energy', property: 'energy.polNum', isNested: true},
  mous: {property: 'mous'},
  publicData: {property: 'releaseDate'},
  objectTypes: {
    objectTypesSimbad: {longtype: 'longtypesSimbad', isBestMatch: 'bestLongtypeSimbad'},
    objectTypesNed: {longtype: 'longtypesNed', isBestMatch: 'bestLongtypeNed'}
  },
  calibratorObservations: {property: 'isScience'}
};
const observationsColumnsSearchFiltersMappingSearchProperties = {
  observationsProjectCode: {property: 'project.code'},
  observationsSourceName: {property: 'sourceName'},
  observationsRa: {property: 'ra'},
  observationsDec: {property: 'dec'},
  observationsBand: {property: 'band'},
  observationsContinuumSensitivity: {property: 'continuumSensitivity'},
  observationsFrequency: {
    path: 'energy',
    property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
    isNested: true
  },
  observationsReleaseDate: {property: 'releaseDate'},
  observationsNumberPublications: {property: 'numberPublications'},
  observationsSpatialResolution: {property: 'spatialResolution'},
  observationsVelocityResolution: {property: 'velocityResolution'},
  observationsArray: {property: 'array'},
  observationsMosaic: {property: 'mosaic'},
  observationsLargestAngularScale: {property: 'largestAngularScale'},
  observationsFov: {property: 'fov'},
  observationsScientificCategory: {property: 'project.scientificCategory'},
  observationsScienceKeyword: {property: 'project.scienceKeyword'},
  observationsIntegration: {property: 'integration'},
  observationsGalacticLongitude: {property: 'galacticLongitude'},
  observationsGalacticLatitude: {property: 'galacticLatitude'},
  observationsFrequencyResolution: {property: 'frequencyResolution'},
  observationsPolProducts: {property: 'polProducts'},
  observationsBaselineL80: {property: 'baselineL80'},
  observationsBaselineL5: {property: 'baselineL5'},
  observationsObservationdDate: {property: 'observationDate'},
  observationsPiName: {property: 'project.piName'},
  observationsSbName: {property: 'sbName'},
  observationsProposalAuthors: {property: 'project.coiName'},
  observationsPwv: {property: 'pwv'},
  observationsGous: {property: 'gous'},
  observationsNumberAsdms: {property: 'numberAsdms'},
  observationsProjectTitle: {property: 'project.title'},
  observationsProjectType: {property: 'project.type'},
  observationsScanIntent: {property: 'scanIntent'},
  observationsQa2Status: {property: 'qa2Status'},
  observationsExternalCollections: {property: 'externalCollections'}
};
const projectsColumnsSearchFiltersMappingSearchProperties = {
  projectsCode: {property: 'project.code'},
  projectsTitle: {property: 'project.title'},
  projectsType: {property: 'project.type'},
  projectsPiName: {property: 'project.piName'},
  projectsProposalAuthors: {property: 'project.coiName'},
  projectsMaxReleaseDate: {property: 'project.releaseDate'},
  projectsNumberPublications: {property: 'project.numberPublications'},
  projectsNumberObservations: {property: 'project.numberObservations'},
  projectsSbNames: {property: 'project.sbNames'}
};
const publicationsColumnsSearchFiltersMappingSearchProperties = {
  publicationsBibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
  publicationsFirstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
  publicationsJournal: {path: 'publications', property: 'publications.journal', isNested: true},
  publicationsYear: {path: 'publications', property: 'publications.publicationYear', isNested: true},
  publicationsTitle: {path: 'publications', property: 'publications.title', isNested: true},
  publicationsMaxReleaseDate: {path: 'publications', property: 'publications.releaseDate', isNested: true},
  publicationsNumberProjects: {path: 'publications', property: 'publications.numberProjects', isNested: true},
  publicationsNumberObservations: {path: 'publications', property: 'publications.numberObservations', isNested: true},
  publicationsAuthors: {path: 'publications', property: 'publications.authors', isNested: true}
};
const aladinPolygonSearchFiltersMappingSearchProperties = {astrofilt: {property: 'footprint'}};

const calculateMOCOrderFromFov = (fov: number): number => {
  if (fov > 100) {
    return 2;
  } else if (fov > 75) {
    return 3;
  } else if (fov > 50) {
    return 4;
  } else if (fov > 25) {
    return 5;
  } else if (fov > 15) {
    return 6;
  }
  return undefined;
};

@Component({
  selector: 'asax-sky-view-aladin-container',
  templateUrl: './aladin-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AladinContainerComponent {
  @ViewChild(AladinSkyComponent, {static: true}) aladin: AladinSkyComponent;

  @Input() height: number;
  @Input() width: number;

  areFootprintsVisible: boolean = true;
  baseImage: Observable<SkyLayer>;
  overlayImage: Observable<SkyLayer>;
  alpha: Observable<number>;
  indexActiveSky: number;

  footprints: Observable<any>;
  skyObjects: Observable<any>;
  cells: Observable<any>;

  fov: Observable<any>;
  raDecFov: Observable<any>;

  rowHovered: Observable<any>;
  sourceNameSearchRadius: Observable<SourceNameSearchRadius>;
  raDecSearchRadius: Observable<SourceNameSearchRadius>;
  footprintsRowHovered: Observable<any>;

  serviceIdGenerator = serviceIdGenerator();
  colorGenerator = colorGenerator();

  voServiceOverlays: VOServiceOverlay[] = [];

  voServiceAdded: Observable<VOServiceOverlay>;
  voServiceRemoved: Observable<string>;

  private voServiceAddSubject = new Subject<VOServiceOverlay>();
  private voServiceRemoveSubject = new Subject<string>();

  constructor(private readonly asaxSkyViewService: AsaxSkyViewService,
              private readonly asaxHeaderService: AsaxHeaderService,
              private readonly asaxResultTableService: AsaxResultTableService,
              private readonly asaxDownloadingService: AsaxDownloadingService,
              private readonly aladinLayersService: AladinSkyLayersService,
              private readonly aladinService: AladinService,
              private readonly aladinFootprintsService: AladinFootprintsService,
              private readonly aladinSkyObjectsService: AladinSkyObjectsService,
              private readonly aladinCellsService: AladinCellsService,
              private readonly asaxVoService: AsaxVoService) {
    this.raDecFov = this.asaxSkyViewService.getRaDecFov();

    this.baseImage = this.aladinLayersService.getBaseSky().pipe(distinctUntilChanged());
    this.overlayImage = this.aladinLayersService.getOverlaySky().pipe(distinctUntilChanged());
    this.alpha = this.aladinLayersService.getAlpha().pipe(distinctUntilChanged());

    this.sourceNameSearchRadius = this.asaxHeaderService.getSourceNameSearchRadius();
    this.raDecSearchRadius = this.asaxHeaderService.getRaDecSearchRadius();

    this.footprints = combineLatest([
      this.aladinService.getFov(),
      this.aladinService.getSearchPolygon(),
      this.asaxHeaderService.getHeaderFilters(),
      this.asaxResultTableService.getColumnsFiltersForActiveTabSubject(),
      this.asaxResultTableService.getActiveTab(),
      this.asaxDownloadingService.getObservationsToBeDownloaded()
    ]).pipe(
      debounceTime(500),
      switchMap(([fov, polygon, filters, columnsFilters, activeTab, observationsToBeDownloaded]) => {
        if (fov <= 15) {
          const filtersMappingSearchProperties = {
            ...headerSearchFiltersMappingSearchProperties,
            ...observationsColumnsSearchFiltersMappingSearchProperties,
            ...projectsColumnsSearchFiltersMappingSearchProperties,
            ...publicationsColumnsSearchFiltersMappingSearchProperties,
            ...aladinPolygonSearchFiltersMappingSearchProperties
          };
          return this.aladinFootprintsService
            .fetchFootprints(polygon, [...filters, ...columnsFilters], filtersMappingSearchProperties)
            .pipe(
              map(footprints => footprints?.map(footprint => ({
                ...footprint,
                selected: asaxDownloadingService.isObservationSelectedToBeDownloaded(footprint.datasetId)
              })))
            );
        } else {
          return of([]);
        }
      })
    );

    this.footprintsRowHovered = combineLatest([
      this.aladinService.getFov(),
      this.aladinService.getSearchPolygon(),
      this.asaxResultTableService.getRowHovered(),
      this.asaxDownloadingService.getObservationsToBeDownloaded()
    ]).pipe(
      debounceTime(250),
      switchMap(([fov, polygon, row, observationsToBeDownloaded]) => {
        if (fov <= 15) {
          return this.aladinFootprintsService.fetchFootprintsForRow(row, polygon)
            .pipe(
              map(footprints => footprints?.map(footprint => ({
                ...footprint,
                selected: asaxDownloadingService.isObservationSelectedToBeDownloaded(footprint.datasetId)
              })))
            );
        } else {
          return of([]);
        }
      })
    );

    this.skyObjects = combineLatest([
      this.aladinService.getFov(),
      this.footprints
    ]).pipe(
      switchMap(([fov, footprints]) => {
        if (fov <= 0.2) {
          const datasetIds = footprints.map(footprint => footprint.datasetId).filter(datasetId => !!datasetId);
          return this.aladinSkyObjectsService.fetchSkyObjects(datasetIds);
        } else {
          return of([]);
        }
      })
    );

    this.cells = combineLatest([
      this.aladinService.getFov().pipe(map(fov => calculateMOCOrderFromFov(fov), distinctUntilChanged())),
      this.aladinService.getSearchPolygon(),
      this.asaxHeaderService.getHeaderFilters(),
      this.asaxResultTableService.getColumnsFiltersForActiveTabSubject(),
      this.asaxResultTableService.getActiveTab(),
    ]).pipe(
      debounceTime(500),
      switchMap(([mocOrder, polygon, filters, columnsFilters, activeTab]) => {
        if (mocOrder) {
          const filtersMappingSearchProperties = {
            ...headerSearchFiltersMappingSearchProperties,
            ...observationsColumnsSearchFiltersMappingSearchProperties,
            ...projectsColumnsSearchFiltersMappingSearchProperties,
            ...publicationsColumnsSearchFiltersMappingSearchProperties,
            ...aladinPolygonSearchFiltersMappingSearchProperties
          };
          return this.aladinCellsService.searchCells(mocOrder, polygon, [...filters, ...columnsFilters], filtersMappingSearchProperties);
        } else {
          return of([]);
        }
      })
    );

    this.voServiceAdded = this.asaxVoService.voServiceGenerated().pipe(
      map(({name, color, id, footprints}) => ({name, color, id, footprints}))
    );

    this.voServiceRemoved = this.asaxVoService.getVoServiceRemoved();
  }

  onOutAladinSearchPolygon(searchPolygon: string): void {
    this.aladinService.setSearchPolygon(searchPolygon);
  }

  onOutAladinFov(fov: number): void {
    this.aladinService.setFov(fov);
  }

  onOutDisplayCells(displayCells: boolean): void {
    this.aladin.displayCells(displayCells);
  }

  onOutDisplayFootprints(displayFootprints: boolean): void {
    this.areFootprintsVisible = displayFootprints;
    this.aladin.displayFootprints(displayFootprints);
  }

  onOutDisplaySkyObjects(displaySkyObjects: boolean): void {
    this.aladin.displaySkyObjects(displaySkyObjects);
  }

  onOutSnapshot(): void {
    this.aladin.snapshot();
  }

  onOutZoomIn(): void {
    this.aladin.zoomIn();
  }

  onOutZoomOut(): void {
    this.aladin.zoomOut();
  }

  onOutAladinMouseMove(mouseMove: any): void {
    this.aladinService.setMouseMove(mouseMove);
  }

  onOutAladinFootprintHovered(footprint: Footprint): void {
    this.asaxSkyViewService.setFootprintHovered(footprint);
  }

  onOutAladinSearchRadiusHovered(footprint: Footprint): void {
    this.asaxSkyViewService.setSearchRadiusHovered(footprint);
  }

  onOutAladinFootprintClicked(footprint: Footprint): void {
    if (footprint.selected) {
      this.asaxDownloadingService.addObservationsToBeDownloaded(footprint.datasetId);
    } else {
      this.asaxDownloadingService.removeObservationsToBeDownloaded(footprint.datasetId);
    }
  }

  onOutAladinSkyObjectHovered(skyObject: SkyObject): void {
    this.aladinSkyObjectsService.setSkyObjectHovered(skyObject);
  }

  onOutContextmenu(mouseEvent: MouseEvent): void {
    this.aladinService.setContextmenu(mouseEvent);
  }

  onOutMouseClick(mouseClick: MouseEvent): void {
    this.aladinService.setMouseClick(mouseClick);
  }

  onOutAddService({url, name}: VOServiceItem) {
    const polygon = this.aladinService.getSearchPolygonValue().split(" ").filter(chuck => !isNaN(+chuck)).join(",");
    const parsedUrl = url
      .replaceAll('$POLYGON', polygon)
      .replaceAll('INTERSECTS(fov,POLYGON(\'ICRS\',0,-90,180))', '1')
      .replaceAll('INTERSECTS(s_region,POLYGON(\'ICRS\',0,-90,180))', '1');

    this.asaxVoService.generateVoService(name, parsedUrl);
  }
}

function* serviceIdGenerator() {
  let index = 0;

  while (true) {
    yield `vo-service-${index}`;
    index++;
  }
}

function* colorGenerator() {
  const letters = '0123456789ABCDEF';

  while (true) {
    let color = '#';

    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }

    yield color;
  }
}
